import React, { useState } from 'react'
import { VeicoloLandingType } from '@next-nx/utils/types'
import { useTrans, useTealium } from '@next-nx/hooks'
import { priceNotation } from '@next-nx/utils/safe'
import { Button, WImage } from '@next-nx/shared-ui/atoms'
import styles from './VeicoloLandingCard.module.sass'
import { ArrowRight } from '@next-nx/shared-ui/icons'
import { getTimestamp } from '@next-nx/utils'
import {
  FormRichiestaPreventivoModal,
  RichiestaPreventivoThankYouModal,
} from '@next-nx/shared-ui/organisms'
import { useCreateRichiestaContattoMutation } from '@next-nx/shared-graphql/promo'


interface Props {
  veicolo: VeicoloLandingType
  variant?: 'default' | 'highlight'
  showIe?: boolean
  isClickable?: boolean
  isCtaClickable?: boolean
  className?: string
}

const VeicoloLandingCard = (props: Props) => {

  const { 
    veicolo,
    variant = 'default',
    isClickable = false,
    isCtaClickable = false,
    showIe = false,
    className = '',
  } = props

  const t = useTrans()
  const { linkDataLayer } = useTealium()


  const [createRichiestaContattoMutation] = useCreateRichiestaContattoMutation()

  const [openFormRichiestaPreventivoModal, setOpenFormRichiestaPreventivoModal] = useState(false)
  const [openRichiestaPreventivoThankYouModal, setOpenRichiestaPreventivoThankYouModal] = useState(
    false
  )
  const [
    statusRichiestaPreventivoThankYouModal,
    setStatusRichiestaPreventivoThankYouModal,
  ] = useState<string | undefined>()

  const handleClick = () => {
    isClickable && setOpenFormRichiestaPreventivoModal(true)
  }

  const handleCtaClick = () => {
    isCtaClickable && setOpenFormRichiestaPreventivoModal(true)
  }

  const handleTracking = () => {
    const action_timestamp = getTimestamp()
  
    linkDataLayer({
       virtual: "action",
       action_name: "richiesta_preventivo", 
       action_tech: "click",
       action_type: "richiedi_preventivo",
       action_timestamp,
       action_effect: "form_richiesta_preventivo_accesso"
    });
  }

  return veicolo ? (
    <div className={`${styles.veicoloLandingCard} ${styles[`veicoloLandingCard--${variant}`]} ${className}`}>
      <div className={`${styles.imageBox}`} role={isClickable ? 'button' : undefined} onClick={handleClick}>
        <WImage 
          src={veicolo.image}
          alt={veicolo.nome}
          title={veicolo.nome}
          maxWidth={variant === 'highlight' ? 540 : 227}
          maxHeight={variant === 'highlight' ? 360 : 163}
          layout="fill"
          objectFit="contain"
          objectPosition="50% 50%"
        />
      </div>
      <div className={styles.content}>
        <div className={styles.innerContent}>
          <p className={styles.title} role={isClickable ? 'button' : undefined} onClick={handleClick} onKeyDown={handleClick} tabIndex={isClickable ? 0 : undefined}>{veicolo.nome}</p>
          {!!veicolo.descrizione && <div className={styles.description} dangerouslySetInnerHTML={{ __html: veicolo.descrizione }} />}
          {!!veicolo.condizioni && <p className={styles.condizioni}>{veicolo.condizioni}</p>}
          {!!veicolo.anticipo ? (
            <p className={styles.anticipo}>{t("Anticipo")}: {priceNotation(veicolo.anticipo)} <span className={styles.anticipoIva}>{t("iva inclusa")}</span></p>
          ) : (
            <p className={styles.senzaAnticipo}>{t("Senza anticipo")}</p>
          )}
          {!!veicolo.prezzoIi && (
            <div className={styles.price}>
              {!!veicolo.prezzoScontatoIi && (
                <span className={styles.fullPrice}>{priceNotation(veicolo.prezzoIi)}</span>
              )}
              <span className={styles.finalPrice}>{priceNotation(veicolo.prezzoScontatoIi ? veicolo.prezzoScontatoIi : veicolo.prezzoIi)}</span>
              <span className={styles.priceLabel}>{t("i.i. al mese")}</span>
            </div>
          )}
          {showIe && !!veicolo.prezzoIe && (
            <div className={`${styles.price} ${styles['price--ie']}`}>
              {!!veicolo.prezzoScontatoIe && (
                <span className={styles.fullPrice}>{priceNotation(veicolo.prezzoIe)}</span>
              )}
              <span className={styles.finalPrice}>{priceNotation(veicolo.prezzoScontatoIe ? veicolo.prezzoScontatoIe : veicolo.prezzoIe)}</span>
              <span className={styles.priceLabel}>{t("i.e. al mese")}</span>
            </div>
          )}
          {!!veicolo.risparmioDipendenteIi && (
            <div className={`${styles.risparmioDipendente}`}>
              <span>{t("Risparmio dipendente")}:</span>
              <span>
                {priceNotation(veicolo.risparmioDipendenteIi)} {t("i.i.")}{' '}
                {veicolo.risparmioDipendenteIe && <>({priceNotation(veicolo.risparmioDipendenteIe)} {t("i.e.")})</>}
              </span>
            </div>
          )}
        </div>
        {(!!veicolo.url || !!isCtaClickable) && (
          <div className={styles.ctaBox}>
            <Button onClick={() => {
              handleCtaClick()
              handleTracking()
              }} href={isCtaClickable ? undefined : veicolo.url} icon={<ArrowRight />} iconPos="right" label={variant === 'highlight' ? t("Richiedi un preventivo") : undefined} />
          </div>
        )}
      </div>
      <FormRichiestaPreventivoModal
        open={openFormRichiestaPreventivoModal}
        setOpen={setOpenFormRichiestaPreventivoModal}
        mutation={createRichiestaContattoMutation}
        onSubmit={(status) => {
          setOpenFormRichiestaPreventivoModal(false)
          setOpenRichiestaPreventivoThankYouModal(true)
          setStatusRichiestaPreventivoThankYouModal(status)
        }}
        modelloVeicolo={veicolo.nome}
      />
      <RichiestaPreventivoThankYouModal
        open={openRichiestaPreventivoThankYouModal}
        setOpen={setOpenRichiestaPreventivoThankYouModal}
        status={statusRichiestaPreventivoThankYouModal}
      />
    </div>
  ) : <></>
}

export default VeicoloLandingCard
